import styled from '@emotion/styled';
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getDateShort } from '@utils/dates';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import { Spacings, breakpoints } from '@styles/index';
import Menu from '@base/Menu/Menu';
import { hasActiveSubscriptionToPlatformBatch } from '@models/Me';
import Dialog from '@comp/Dialog/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { savedObjectTypes, BACKEND_API_ERROR } from '@constants/index';
import { removeEntityFromSaved } from '@actions/saved';
import { getSavedContentType } from '@utils/segment/propsHelper';
import { SAVE_UNSAVE_CONTENT } from '@constants/segment';
import { getBatchGroupSubscriptions } from '@selectors/auth';
import { segment } from '@actions/analytics';
import pushRoute from '@utils/pushRoute';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSnackbar } from '@stores/snackbar';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline,
  getPlusSubscriptionInfo
} from '@selectors/subscription';

import {
  isPost,
  isCompletedWithNoVideoUrl,
  isPostClickable
} from '@models/Item';
import usePlatformInfo from '@hooks/usePlatformInfo';
import ItemCard from '../Course/ItemCard';
import PDFDownload from '../Course/PDFDownload';

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  cursor: pointer;
`;

const DateHeader = styled(H4)`
  margin-bottom: ${Spacings.SPACING_2B};
  background-color: var(--color-base-0);
  padding: ${Spacings.SPACING_4B} 0;
  z-index: 1;
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    background-color: inherit;
  }
`;

const BatchScheduleItem = ({ item, index, goalUID, isNewDate }) => {
  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const dateString = `${getDateShort(item.liveAt, false)}`;
  const subscription = useSelector(getPlusSubscriptionInfo(goalUID));
  const isSubscribed = useSelector(getIsSubscribedToPlus(goalUID));
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isUnEnrollModalOpen, setIsUnEnrollModalOpen] = useState(false);
  const { isPlatform } = usePlatformInfo();
  const batchGroupSubscriptions = useSelector(getBatchGroupSubscriptions);
  const { query } = useRouter();
  const { batchUID: batchGroupUID } = query;
  const isSubscribedToBatchGroup = hasActiveSubscriptionToPlatformBatch(
    batchGroupSubscriptions,
    batchGroupUID
  );

  const setPDF = (elem, lesson) => {
    setAnchorEl(elem);
    setSelectedLesson(lesson);
  };
  const showRemoveDialog = (lesson) => {
    setSelectedLesson(lesson);
    setIsUnEnrollModalOpen(true);
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMenuAnchorEl(null);
  }, []);
  const showActionMenu = (elem, lesson) => {
    setMenuAnchorEl(elem);
    setSelectedLesson(lesson);
  };

  const isPlusLessonClickable = (lesson) => {
    if (isCompletedWithNoVideoUrl(lesson)) return false;
    return isPostClickable(lesson);
  };

  const onItemClick = (lesson) => {
    const { type, slug, uid } = lesson;
    if (type === 'post' && isPlusLessonClickable(lesson)) {
      window.open(`/class/${slug}/${uid}`, '_blank');
    } else if (type === 'quiz') {
      window.open(`/quiz/${slug}/${uid}`, '_blank');
    }
  };
  const onCloseModal = () => {
    setIsUnEnrollModalOpen(false);
    setSelectedLesson(null);
  };
  const goToCoursePage = () => {
    if (!selectedLesson) return;
    const { type } = selectedLesson;
    pushRoute(
      `/${type === 'post' ? 'course' : 'test-series'}/[slug]/[courseUID]`,
      `/${type === 'post' ? 'course' : 'test-series'}/${
        selectedLesson.courseSlug
      }/${selectedLesson.courseUID}`
    );
    handleClose();
  };
  const removeFromCollection = () => {
    if (selectedLesson) {
      // const goalUID = selectedLesson.goal?.uid || selectedLesson.goalUid;
      const isLesson = isPost(selectedLesson);
      const entityType = isLesson
        ? savedObjectTypes.LESSON
        : savedObjectTypes.TEST;
      dispatch(
        removeEntityFromSaved({
          goalUid: goalUID,
          objectUid: selectedLesson.uid,
          entityType,
          key: `${goalUID}-${isLesson ? 'lessons' : 'tests'}`
        })
      ).then((res) => {
        if (res?.result) {
          showSnackbar({
            message: `${isLesson ? 'Lesson' : 'Test'} removed successfully`,
            time: 5000,
            type: 'success'
          });
          segment.track(SAVE_UNSAVE_CONTENT, {
            save_content_type: getSavedContentType(entityType),
            save_content_uid: selectedLesson.uid,
            save_content_title: selectedLesson.name,
            ...goalSegmentData
          });
        } else {
          showSnackbar({
            message: BACKEND_API_ERROR,
            time: 5000,
            type: 'error'
          });
        }
      });
    }
    onCloseModal();
  };

  return (
    <>
      <Dialog
        onClose={onCloseModal}
        dialogState={isUnEnrollModalOpen}
        heading="Remove from Saved?"
        message={`This will remove the ${
          isPost(selectedLesson) ? 'lesson' : 'test'
        } from your saved list. You can add it back from the ${
          isPost(selectedLesson) ? 'course' : 'test series'
        } page.`}
        messageWidth="82.5%"
        acceptLabel="Remove"
        rejectLabel="Cancel"
        acceptOnClick={removeFromCollection}
        rejectOnClick={onCloseModal}
      />
      <Menu
        anchorEl={anchorEl}
        onClose={handleClose}
        position="right"
        width="231px"
      >
        <PDFDownload
          selectedLesson={selectedLesson}
          handleClose={handleClose}
        />
      </Menu>
      <Menu
        anchorEl={menuAnchorEl}
        onClose={handleClose}
        position="right"
        width="231px"
      >
        <MenuItem onClick={goToCoursePage}>
          <P1 color="var(--color-text-primary)">Go to course page</P1>
        </MenuItem>
      </Menu>
      <div key={`class-item-${item.uid}`}>
        {isNewDate[index] && (
          <DateHeader
            id={`date-${new Date(item.liveAt).getDate()}-${new Date(
              item.liveAt
            ).toLocaleString('default', { month: 'short' })}`}
            className="date-stamp"
            data-date={new Date(item.liveAt).getTime()}
          >
            {dateString}
          </DateHeader>
        )}
        <ItemCard
          key={item.uid}
          lesson={item}
          forPlus
          cardPosition={-1}
          isBatch
          isSubscribed={
            isSubscribed || isSubscribedToBatchGroup || isOfflineSubscription
          }
          subscription={subscription}
          setPdfAnchorEl={setPDF}
          setAnchorEl={showActionMenu}
          onItemClick={onItemClick}
          showRemoveDialog={showRemoveDialog}
          showMoreMenu={!isPlatform}
        />
      </div>
    </>
  );
};

export default BatchScheduleItem;
BatchScheduleItem.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  index: PropTypes.number.isRequired,
  goalUID: PropTypes.string.isRequired,
  isNewDate: PropTypes.arrayOf(PropTypes.any).isRequired
};
